/**
 * AtlasDroidCommandModule — deployed droids on the Commons (the SWG datapad-pet lineage) and THE DROID
 * CLASSES (2026-09-24).
 *
 *   the world   every deployed droid renders at its authoritative position: the class's generated model
 *               (`prop:droid_<class>`, Higgsfield → Meshy 7) fitted to the class's real size — a medic at
 *               1.7 m, a mega battle droid at 8.5 m — turned so its FRONT follows the server's yaw
 *               (lib/droid_view.ts MODEL_YAW), standing on the ground (the medic hovers); a plate with the
 *               name and a health bar; speech bubbles from `droid_say` (says, adverts, greetings, sales).
 *   K           at your own droid (10 m from its HULL — droidReachM; a mega tank reaches 14 m from its centre): the
 *               datapad on it. At someone's vendor droid (12 m from its hull): its stall.
 *               Anywhere else: the datapad (every droid you own). The menu bar's Droids entry too.
 *
 * WHERE A DROID IS (the gaps wave, round 2, 2026-09-28): the narrow `droid_pos` row (written by every step that moves
 * it, with the step's stamps) — never the unit row, whose position is a snapshot now; `droid_pos` and the owner's
 * `droid_purse` are subscribed here (the base subscription keeps the unit, vitals, cargo, say and route rows).
 *
 * The server runs the droids on its own clock (droid_tick); this module only draws them. It no longer calls
 * `tickDroids` (2026-09-25): the clock renews itself while any droid works, and every reducer that sets one working
 * (program, run/come/follow…, fund) ensures it server-side — the once-a-second call was a transaction per client
 * per second for nothing.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { buildDroidMesh } from './AtlasDroidModule.js';
import { groundYIn } from '../lib/grounding.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { prefabAssetId } from '../lib/player_models.js';
import { modelYawOf, renderSizeOf, baseLiftOf, classOfKind, livePosOf } from '../lib/droid_view.js';
import { DROID_CLASSES, droidReachM } from '../lib/droid_classes.js';
import { DroidTrack } from '../lib/droid_motion.js';
import { themeColor, currentTheme } from '../lib/ui_theme.js';
import { DroidDatapad } from './droid/DroidDatapad.js';
import { DroidShop } from './droid/DroidShop.js';
import type { AssetModule } from './AssetModule.js';

const TERMINAL_RANGE_M = 10;
const SHOP_RANGE_M = 12; // mirrors droid_host SHOP_RANGE_M (the server's buyFromDroid reach) — this said 6, so the stall prompt vanished at half the range the server sells at
const PLATE_RANGE_M = 40;
const BUBBLE_S = 6;
const BUBBLE_RANGE_M = 45;

interface DroidRow {
  droidId: bigint;
  owner: { toHexString(): string; isEqual(o: unknown): boolean };
  worldId: bigint;
  kind: string;
  name: string;
  mode: string;
  posX: number;
  posZ: number;
  yaw: number;
  programSrc: string;
  logJson: string;
}

interface Unit {
  obj: THREE.Group;
  kind: string;
  bob: number;
  fallback: boolean;
  plate: THREE.Sprite;
  plateKey: string;
  bubble: THREE.Sprite | null;
  bubbleId: bigint;
  top: number;
}

function canvasSprite(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void, worldW: number): THREE.Sprite {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d');
  if (g) draw(g);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  const s = new THREE.Sprite(mat);
  s.scale.set(worldW, worldW * (h / w), 1);
  s.renderOrder = 5;
  return s;
}

function disposeSprite(s: THREE.Sprite | null): void {
  if (!s) return;
  s.removeFromParent();
  (s.material as THREE.SpriteMaterial).map?.dispose();
  s.material.dispose();
}

export class AtlasDroidCommandModule implements IModule {
  readonly name = 'AtlasDroidCommand';

  private app: App | null = null;
  private assets: AssetModule | null = null;
  private units = new Map<string, Unit>();
  /** Each droid's playback of its server positions (lib/droid_motion) — kept apart from the unit so a model
   *  swap (the placeholder replaced when the cooked model lands) never jumps the droid. */
  private tracks = new Map<string, DroidTrack>();
  /** droid_pos row events are wired (once per connection — the SDK hands out no unsubscribe). */
  private posEvents = false;
  /** The wall clock of the last frame (ms) — the playback's pace (see `frame`). */
  private lastWallMs = 0;
  private masters = new Map<string, THREE.Object3D>();
  private loading = new Set<string>();
  private hint: HTMLDivElement | null = null;
  private hintText = '';
  private datapad = new DroidDatapad();
  private shop = new DroidShop();
  private posSubbed = false;

  constructor(assets: AssetModule | null = null) {
    this.assets = assets;
  }

  /** The class's generated GLB (prop:<kind>) fitted to the class size and turned to face +Z; null while it loads. */
  private droidMaster(kind: string): THREE.Object3D | null {
    const have = this.masters.get(kind);
    if (have) return have;
    const conn = atlas.conn;
    if (!conn || !this.assets || this.loading.has(kind)) return null;
    const id = prefabAssetId(conn, `prop:${kind}`);
    if (id === null) return null;
    this.loading.add(kind);
    this.assets.load(id)
      .then((cached) => {
        // Never mutate the shared AssetModule cache (SpaceModule docks the same droid GLB at the ship's
        // droid port) — normalise a private clone: longest edge = the class size, base at y 0, front +Z.
        const inner = cached.clone();
        inner.rotation.y = modelYawOf(kind);
        inner.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(inner);
        const size = new THREE.Vector3();
        box.getSize(size);
        const longest = Math.max(size.x, size.y, size.z);
        const k = longest > 1e-3 ? renderSizeOf(kind) / longest : 1;
        const wrap = new THREE.Group();
        inner.scale.multiplyScalar(k);
        const c = new THREE.Vector3();
        box.getCenter(c);
        inner.position.set(-c.x * k, -box.min.y * k, -c.z * k);
        wrap.add(inner);
        wrap.userData.height = size.y * k;
        markShared(wrap);
        this.masters.set(kind, wrap);
      })
      .catch(() => { /* the primitive covers it */ })
      .finally(() => { this.loading.delete(kind); });
    return null;
  }

  /** droid_pos (everyone's — where every droid is) and droid_purse (the owner's money; row security trims it). */
  private subscribePos(): void {
    const conn = atlas.conn;
    if (!conn || this.posSubbed) return;
    this.posSubbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM droid_pos', 'SELECT * FROM droid_purse WHERE owner = :sender']); }
    catch { this.posSubbed = false; /* pre-publish bindings */ }
    // EVERY SAMPLE, IN ORDER (2026-09-28): the frame loop reads the row's CURRENT state, so one long frame (a hitch, a
    // busy main thread) swallowed the steps between — and the newest step's `fromAt` then read as a STAND followed by
    // a sprint across the missed steps (measured in the browser: 13.4 m/s frames after a 1.6 s hitch, 67 % of frames
    // steady). The row events see every step; the frame loop's read stays as the fallback (a repeated sample is a no-op).
    if (!this.posEvents) {
      try {
        const onPos = (row: { droidId: bigint; x: number; z: number; stepAt: { microsSinceUnixEpoch: bigint }; fromAt: { microsSinceUnixEpoch: bigint } }): void => {
          const track = this.tracks.get(row.droidId.toString());
          if (track) track.push(Number(row.stepAt.microsSinceUnixEpoch) / 1e6, row.x, row.z, Number(row.fromAt.microsSinceUnixEpoch) / 1e6);
        };
        conn.db.droidPos.onInsert((_c, row) => onPos(row));
        conn.db.droidPos.onUpdate((_c, _old, row) => onPos(row));
        this.posEvents = true;
      } catch { /* pre-publish bindings */ }
    }
  }

  /** Where a droid IS: its droid_pos row first (the unit row's position is a snapshot). */
  private posOf(d: DroidRow): { x: number; z: number; yaw: number } {
    let p = null;
    try { p = atlas.conn?.db.droidPos.droidId.find(d.droidId) ?? null; } catch { /* pre-publish */ }
    return livePosOf(p, d);
  }

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('DroidUnits', Phase.Animation, 40, (dt) => this.frame(dt)));
    bus.on('atlas:account-ready', () => this.subscribePos());
    this.subscribePos();
    window.addEventListener('keydown', this.onKeyDown);
    bus.on('atlas:world-cleared', () => { this.datapad.close(); this.shop.close(); this.clearUnits(); });
    bus.on('atlas:open-droids', (e) => this.datapad.open(e?.droidId ? BigInt(e.droidId) : undefined, (e?.tab as never) ?? undefined));
    const hint = document.createElement('div');
    hint.style.cssText = 'position:fixed;bottom:296px;left:50%;transform:translateX(-50%);z-index:40;'
      + 'font:600 13px var(--ax-font);color:var(--ax-hi);background:color-mix(in srgb, var(--ax-bg) 72%, transparent);border:1px solid var(--ax-line);'
      + 'border-radius:var(--ax-radius);padding:5px 12px;display:none;pointer-events:none';
    document.body.appendChild(hint);
    this.hint = hint;
    // Probe/dev lever.
    (window as unknown as Record<string, unknown>).__droid = {
      deploy: (itemId: string) => atlas.conn?.reducers.deployDroid({ itemId: BigInt(itemId) }),
      open: (tab?: string) => { const n = this.nearestOwnDroid(); this.datapad.open(n?.droidId ?? null, tab as never); return n?.droidId?.toString() ?? null; },
      datapad: () => this.datapad,
      shop: (id?: string) => { const n = id ? BigInt(id) : this.nearestShop()?.droidId; if (n !== undefined && n !== null) this.shop.open(n); return n?.toString() ?? null; },
      units: () => [...this.units.keys()],
      unitInfo: () => [...this.units.entries()].map(([id, u]) => ({
        id, kind: u.kind, fallback: u.fallback, size: renderSizeOf(u.kind), top: u.top,
        bubble: u.bubble ? true : false, plate: u.plate.visible, pos: [u.obj.position.x, u.obj.position.y, u.obj.position.z],
        wall: this.lastWallMs, // the frame that placed it (the probe's clock: rAF callbacks run apart from the engine's)
      })),
    };
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat || e.code !== 'KeyK' || e.ctrlKey || e.metaKey || e.altKey) return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (atlas.spaceFlight || atlas.flyMode || atlas.loadingGate) return;
    if (this.shop.isOpen()) { this.shop.close(); return; }
    const own = this.nearestOwnDroid();
    if (own) { this.datapad.toggle(own.droidId); return; }
    const vend = this.nearestShop();
    if (vend) { this.shop.open(vend.droidId); return; }
    this.datapad.toggle(null);
  };

  // ─── data ────────────────────────────────────────────────────────────────

  private rows(): DroidRow[] {
    const conn = atlas.conn;
    const out: DroidRow[] = [];
    if (!conn || atlas.currentWorldId === null) return out;
    try {
      for (const d of conn.db.droidUnit.iter()) if (d.worldId === atlas.currentWorldId) out.push(d as unknown as DroidRow);
    } catch { /* row cache mid-churn */ }
    return out;
  }

  private nearestOwnDroid(rows: DroidRow[] = this.rows()): DroidRow | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    let best: DroidRow | null = null;
    let bestD = Infinity;
    for (const d of rows) {
      if (!d.owner.isEqual(conn.identity)) continue;
      const p = this.posOf(d);
      const dist = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
      if (dist > droidReachM(d.kind, TERMINAL_RANGE_M)) continue; // the terminal's reach is from the HULL (a mega is 8.5 m long)
      if (dist < bestD) { bestD = dist; best = d; }
    }
    return best;
  }

  /** The nearest droid of SOMEONE ELSE with priced cargo within the shop range. */
  private nearestShop(rows: DroidRow[] = this.rows()): DroidRow | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    let best: DroidRow | null = null;
    let bestD = Infinity;
    for (const d of rows) {
      if (d.owner.isEqual(conn.identity)) continue;
      const p = this.posOf(d);
      const dist = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
      // The server's buyFromDroid reach (droidReachM — from the hull); this offered the stall out to 8 m while the server refused past 6.
      if (dist > droidReachM(d.kind, SHOP_RANGE_M) || dist >= bestD) continue;
      let priced = false;
      try { for (const e of conn.db.droidCargo.droidCargoByDroid.filter(d.droidId)) if (e.priceSov > 0n) { priced = true; break; } } catch { /* churn */ }
      if (priced) { bestD = dist; best = d; }
    }
    return best;
  }

  // ─── render ──────────────────────────────────────────────────────────────

  private buildUnit(kind: string): { obj: THREE.Group; fallback: boolean; top: number } {
    const master = this.droidMaster(kind);
    const obj = new THREE.Group();
    if (master) {
      obj.add(master.clone());
      return { obj, fallback: false, top: Number(master.userData.height ?? renderSizeOf(kind)) };
    }
    obj.add(buildDroidMesh(kind));
    return { obj, fallback: true, top: 0.5 };
  }

  private plateFor(d: DroidRow): { key: string; draw: (g: CanvasRenderingContext2D) => void } {
    const conn = atlas.conn;
    const v = conn?.db.droidVitals.droidId.find(d.droidId);
    const frac = v ? Math.max(0, Math.min(1, v.hp / Math.max(1, v.maxHp))) : 1;
    const mine = !!conn?.identity && d.owner.isEqual(conn.identity);
    const cls = DROID_CLASSES[classOfKind(d.kind)];
    // The worn theme is part of the key (2026-09-25): a plate drawn in the old theme's colours kept them after the
    // player picked another one, until the droid's health or mode happened to change.
    const key = `${d.name}|${Math.round(frac * 40)}|${d.mode}|${mine}|${currentTheme()}`;
    return {
      key,
      draw: (g) => {
        // The colours are read when the plate is drawn (on a key change), not on every frame the plate is visible.
        const hi = themeColor('--ax-hi'), muted = themeColor('--ax-muted'), bg = themeColor('--ax-bg');
        const good = themeColor('--ax-good'), warn = themeColor('--ax-warn'), bad = themeColor('--ax-bad'), line = themeColor('--ax-line');
        const font = themeColor('--ax-font');
        g.clearRect(0, 0, 512, 128);
        g.fillStyle = bg; g.globalAlpha = 0.78;
        g.fillRect(56, 18, 400, 92);
        g.globalAlpha = 1;
        g.strokeStyle = line; g.lineWidth = 3; g.strokeRect(56, 18, 400, 92);
        g.textAlign = 'center';
        g.fillStyle = hi; g.font = `600 30px ${font}`;
        g.fillText(d.name.slice(0, 22), 256, 54);
        g.fillStyle = muted; g.font = `400 20px ${font}`;
        g.fillText(`${cls.name}${d.mode === 'down' ? ' · disabled' : d.mode === 'idle' ? '' : ` · ${d.mode}`}`, 256, 80);
        g.fillStyle = line; g.fillRect(96, 90, 320, 10);
        g.fillStyle = frac < 0.25 ? bad : frac < 0.6 ? warn : good;
        g.fillRect(96, 90, 320 * frac, 10);
      },
    };
  }

  private bubbleFor(text: string, kind: string): THREE.Sprite {
    const bg = themeColor('--ax-bg'), textC = themeColor('--ax-text');
    const edge = kind === 'advert' ? themeColor('--ax-accent') : kind === 'greet' ? themeColor('--ax-info') : kind === 'sale' ? themeColor('--ax-good') : themeColor('--ax-line2');
    const font = themeColor('--ax-font');
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let cur = '';
    for (const w of words) { if ((cur + ' ' + w).trim().length > 34) { lines.push(cur.trim()); cur = w; } else cur += ` ${w}`; }
    if (cur.trim()) lines.push(cur.trim());
    const shown = lines.slice(0, 4);
    const h = 40 + shown.length * 34;
    return canvasSprite(512, 256, (g) => {
      g.clearRect(0, 0, 512, 256);
      const y0 = 256 - h - 20;
      g.fillStyle = bg; g.globalAlpha = 0.9;
      g.beginPath(); g.roundRect(16, y0, 480, h, 16); g.fill();
      g.globalAlpha = 1;
      g.strokeStyle = edge; g.lineWidth = 4; g.stroke();
      g.beginPath(); g.moveTo(240, y0 + h); g.lineTo(256, y0 + h + 18); g.lineTo(272, y0 + h); g.fillStyle = edge; g.fill();
      g.fillStyle = textC; g.font = `500 28px ${font}`; g.textAlign = 'center';
      shown.forEach((l, i) => g.fillText(l, 256, y0 + 42 + i * 34));
    }, 3.6);
  }

  private frame(dt: number): void {
    const conn = atlas.conn;
    const scene = this.app?.world.scene;
    if (!conn || !scene) return;
    // No client `tickDroids` (2026-09-25): the server's droid_tick renews itself and every waking reducer ensures it —
    // this sent one transaction per client per second while any of your droids worked.
    const rows = this.rows();
    const seen = new Set<string>();
    const nowMs = Date.now();
    // THE PLAYBACK KEEPS WALL TIME: the samples are stamped by the server's clock, and the engine clamps a frame's dt
    // to five fixed steps (0.083 s) — under a slow renderer (200 ms frames) the playhead ran at 0.42× real time, fell
    // behind and caught up in lurches (the browser probe measured 0.16–22.5 m/s). Real elapsed time, ≤ 1 s a frame
    // (a hidden tab is the track's own catch-up) — the fauna's WALL-CLOCK law.
    const wallNow = performance.now();
    const wallDt = this.lastWallMs > 0 ? Math.min(1, Math.max(0, (wallNow - this.lastWallMs) / 1000)) : dt;
    this.lastWallMs = wallNow;
    // The newest speech per droid (droid_say rows, pruned server-side at 60 s).
    const speech = new Map<string, { id: bigint; text: string; kind: string; atMs: number }>();
    try {
      for (const s of conn.db.droidSay.iter()) {
        if (s.worldId !== atlas.currentWorldId) continue;
        const atMs = Number(s.at.microsSinceUnixEpoch / 1000n);
        if (nowMs - atMs > BUBBLE_S * 1000) continue;
        const k = s.droidId.toString();
        const cur = speech.get(k);
        if (!cur || s.sayId > cur.id) speech.set(k, { id: s.sayId, text: s.text, kind: s.kind, atMs });
      }
    } catch { /* churn */ }
    for (const d of rows) {
      const key = d.droidId.toString();
      seen.add(key);
      let u = this.units.get(key);
      if (u && u.fallback && this.masters.has(d.kind)) { this.dropUnit(key, u); u = undefined; }
      if (!u) {
        const built = this.buildUnit(d.kind);
        const plate = canvasSprite(512, 128, () => { /* drawn below */ }, 2.4);
        built.obj.add(plate);
        u = { obj: built.obj, kind: d.kind, bob: Math.random() * 6, fallback: built.fallback, plate, plateKey: '', bubble: null, bubbleId: -1n, top: built.top };
        scene.add(u.obj);
        this.units.set(key, u);
      }
      u.bob += dt;
      // PLAYBACK, not a chase (2026-09-24, lib/droid_motion): the server steps a droid once a second, and the old
      // `1 − 0.002^dt` chase crossed each step in half a second and then stood — jolt, wait, jolt. The track replays
      // the server's samples at their own stamps (droid_vitals.lastStepAt, written with the position) about one
      // interval behind: a steady glide at the droid's real speed that stops exactly where the server stopped it.
      let track = this.tracks.get(key);
      if (!track) { track = new DroidTrack(); this.tracks.set(key, track); }
      // THE POSITION ROW (2026-09-28): droid_pos carries the step's stamp and when its walk began (the droid stood
      // until then) — the unit row is no longer rewritten while a droid walks.
      let pr = null;
      try { pr = conn.db.droidPos.droidId.find(d.droidId) ?? null; } catch { /* pre-publish */ }
      if (pr) track.push(Number(pr.stepAt.microsSinceUnixEpoch) / 1e6, pr.x, pr.z, Number(pr.fromAt.microsSinceUnixEpoch) / 1e6);
      else if (track.newest === null || Math.hypot(track.x - d.posX, track.z - d.posZ) > 1e-3) track.reset(track.newest ?? 0, d.posX, d.posZ); // never moved: stand where the row says
      // Where it walks from that sample (droid_route) — bridges a late tick; never for a parked or downed droid.
      const rt = d.mode === 'idle' || d.mode === 'down' ? null : conn.db.droidRoute.droidId.find(d.droidId);
      track.setRoute(rt ? { t: Number(rt.at.microsSinceUnixEpoch) / 1e6, gx: rt.goalX, gz: rt.goalZ, speed: rt.speed } : null);
      const at = track.advance(wallDt);
      u.obj.position.x = at.x;
      u.obj.position.z = at.z;
      const ground = groundYIn(conn, atlas.currentWorldId, u.obj.position.x, u.obj.position.z);
      const lift = baseLiftOf(d.kind, !u.fallback);
      const bobAmp = u.fallback || lift > 0 ? 0.07 : 0;
      u.obj.position.y = ground + lift + Math.sin(u.bob * 2.1) * bobAmp;
      // Turn smoothly toward the way it is visibly walking (the row's yaw belongs to the NEWEST sample, a second
      // ahead of the playback — it would turn early); standing, toward the row's yaw. Shortest arc.
      let dy = (track.heading ?? (pr ? pr.yaw : d.yaw)) - u.obj.rotation.y;
      if (dy > 1e6 || dy < -1e6) dy = 0;
      dy = ((dy + Math.PI) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
      u.obj.rotation.y += dy * Math.min(1, dt * 6);
      const dist = Math.hypot(at.x - atlas.localX, at.z - atlas.localZ);
      // Plate (name + health), redrawn only when it changes.
      u.plate.visible = dist < PLATE_RANGE_M * Math.max(1, renderSizeOf(d.kind) / 2);
      u.plate.position.set(0, u.top + 0.55, 0);
      u.plate.rotation.set(0, 0, 0);
      if (u.plate.visible) {
        const pl = this.plateFor(d);
        if (pl.key !== u.plateKey) {
          u.plateKey = pl.key;
          const tex = (u.plate.material as THREE.SpriteMaterial).map as THREE.CanvasTexture;
          const g = (tex.image as HTMLCanvasElement).getContext('2d');
          if (g) { pl.draw(g); tex.needsUpdate = true; }
        }
      }
      // Speech bubble.
      const sp = speech.get(key);
      if (sp && dist < BUBBLE_RANGE_M * (sp.kind === 'advert' ? 1 : 0.6)) {
        if (sp.id !== u.bubbleId) {
          disposeSprite(u.bubble);
          u.bubble = this.bubbleFor(sp.text, sp.kind);
          u.bubbleId = sp.id;
          u.obj.add(u.bubble);
        }
        if (u.bubble) u.bubble.position.set(0, u.top + 2.2, 0);
      } else if (u.bubble) { disposeSprite(u.bubble); u.bubble = null; u.bubbleId = -1n; }
    }
    for (const [key, u] of this.units) if (!seen.has(key)) this.dropUnit(key, u);
    for (const key of [...this.tracks.keys()]) if (!seen.has(key)) this.tracks.delete(key);
    // Terminal hint — DOM written only when the text changes.
    if (this.hint) {
      let text = '';
      if (!atlas.spaceFlight && !this.datapad.isOpen() && !this.shop.isOpen()) {
        const own = this.nearestOwnDroid(rows);
        if (own) text = `K — droid terminal (${own.name})`;
        else { const v = this.nearestShop(rows); if (v) text = `K — browse ${v.name}'s wares`; }
      }
      if (text !== this.hintText) {
        this.hintText = text;
        this.hint.textContent = text;
        this.hint.style.display = text ? 'block' : 'none';
      }
    }
  }

  private dropUnit(key: string, u: Unit): void {
    disposeSprite(u.plate);
    disposeSprite(u.bubble);
    this.app?.world.scene.remove(u.obj);
    deferDisposeTree(u.obj);
    this.units.delete(key);
  }

  private clearUnits(): void {
    for (const [key, u] of [...this.units]) this.dropUnit(key, u);
    this.tracks.clear();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.datapad.dispose();
    this.shop.dispose();
    this.clearUnits();
    this.hint?.remove();
  }
}
